import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Spinner, SpinnerType } from '@nx-frontend/shared/ui/spinner';
import { AgentThreadInput, AIAgent, ChatbotUserUploadedFile } from '@nx-frontend/shared/types';
import { uploadFile } from '@nx-frontend/shared/utils/upload-file';
import { AgentModeFormFields } from './agent-mode-form-fields';
import { useGetInvalidFields } from './agent-mode/get-invalid-fields';
import { AgentInput } from './types';

interface AgentModeFormProps {
  onSubmit: (input: AgentInput) => void;
  agent: AIAgent;
  creatingThreadInProgress?: boolean;
  defaults?: AgentThreadInput;
}

export const AgentModeForm: FC<AgentModeFormProps> = ({
  agent,
  onSubmit,
  creatingThreadInProgress = false,
  defaults,
}) => {
  const [inputsWithErrors, setInputsWithErrors] = useState<{ id: string; error: string }[]>([]);
  const [input, setInput] = useState<Record<string, string | File>>({});
  const [cancelledDefaultValues, setCancelledDefaultValues] = useState<string[]>([]);
  const onInputFieldsChange = useCallback((fields: Record<string, string | File>) => {
    setInput(fields);
  }, []);

  const cancelDefaultValue = useCallback(
    (fieldId: string) => {
      setCancelledDefaultValues([...cancelledDefaultValues, fieldId]);
    },
    [cancelledDefaultValues]
  );

  const defaultsActual = useMemo(() => {
    const result: AgentThreadInput = {};
    for (const key in defaults) {
      if (cancelledDefaultValues.includes(key)) {
        continue;
      }
      result[key] = defaults[key];
    }

    return result;
  }, [cancelledDefaultValues, defaults]);

  const getInvalidFields = useGetInvalidFields({
    agent,
    input,
    defaults: defaultsActual,
  });

  const onGenerateClick = useCallback(async () => {
    const invalidFields = getInvalidFields();
    if (invalidFields && invalidFields.length) {
      setInputsWithErrors(invalidFields);
    } else {
      setInputsWithErrors([]);
      const inputWithFilesSaved: AgentInput = {};
      // save files that are in the input as tempStoredFiles
      for (const key in input) {
        if (input[key] instanceof File) {
          const tempStoredFile = await uploadFile(input[key] as File, {
            folder: 'ai_agent',
          });
          if (tempStoredFile) {
            inputWithFilesSaved[key] = {
              filename: tempStoredFile.filename,
              name: tempStoredFile.name,
            };
          }
          // const file = input[key] as File;
        } else if (typeof input[key] === 'string') {
          inputWithFilesSaved[key] = input[key] as string;
        }
      }
      // fill in default values
      for (const key in defaultsActual) {
        if (key in inputWithFilesSaved) {
          continue;
        }
        if (typeof defaultsActual[key] === 'string') {
          inputWithFilesSaved[key] = defaultsActual[key] as string;
        } else if (typeof defaultsActual[key] === 'object') {
          const defaultFile = defaultsActual[key] as ChatbotUserUploadedFile;
          inputWithFilesSaved[key] = {
            filename: defaultFile.filename,
            sfUUID: defaultFile.sfUUID,
          };
        }
      }

      onSubmit(inputWithFilesSaved);
    }
  }, [getInvalidFields, onSubmit, input, defaultsActual]);

  return (
    <AgentModeFormContainer className="!px-2">
      <div className="overflow-y-auto px-2 flex flex-col min-h-0 max-h-full">
        <div className="text-[16px] leading-[148%] text-neutral-800">{agent.description}</div>
        <AgentModeFormFields
          agent={agent}
          onFieldsChange={onInputFieldsChange}
          inputsWithErrors={inputsWithErrors}
          defaults={defaultsActual}
          cancelDefaultValue={cancelDefaultValue}
        />
      </div>
      <div className="flex justify-center mt-[18px]">
        <Button
          className="flex justify-center gap-2"
          type={ButtonType.FLAT}
          size={ButtonSize.MD}
          onClick={onGenerateClick}
        >
          {agent.runButtonTitle || 'Generate'}
          {creatingThreadInProgress && <Spinner width={14} height={14} type={SpinnerType.WHITE} />}
        </Button>
      </div>
    </AgentModeFormContainer>
  );
};

export const AgentModeStartView = (props: { agent: AIAgent; onRunClick: () => void }) => {
  return (
    <AgentModeFormContainer>
      <div className="text-[16px] leading-[148%] text-neutral-800">{props.agent.description}</div>
      <div className="flex justify-center mt-[18px]">
        <Button type={ButtonType.FLAT} size={ButtonSize.MD} onClick={props.onRunClick}>
          {props.agent.runButtonTitle || 'Generate'}
        </Button>
      </div>
    </AgentModeFormContainer>
  );
};

export const AgentModeFormContainer = ({
  children,
  containerClassName,
  className,
}: {
  children: React.ReactNode;
  containerClassName?: string | string[];
  className?: string | string[];
}) => {
  return (
    <div
      className={classNames('flex flex-col justify-end min-h-0 max-h-full', 'pt-5 w-full h-full', containerClassName)}
    >
      <div
        className={classNames(
          'flex flex-col min-h-0 max-h-full',
          'max-w-2xl w-full p-4 mx-auto rounded-lg',
          'border border-[zinc-100]',
          'bg-[radial-gradient(191.81%_165.86%_at_70.71%_25.88%,_#FAFAFA_0%,_#F0FDFA_100%)]',
          className
        )}
      >
        {children}
      </div>
    </div>
  );
};
